import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TEAM_FACTION_NAMES, DISPLAY_TEAM_BORDER, DISPLAY_TEAM_TEXT } from '../displayTheme';

interface SheetVoteDisplayProps {
  sessionId: string;
  showClosedVotes?: boolean; // If true, show closed votes (for results mode)
  activeTeam?: string; // If set, only show this team's vote (for SEQUENTIAL mode)
}

interface VoteResults {
  voteId: string;
  teamColor: string;
  totalVotes: number;
  results: Array<{
    option: { type: 'sheet' | 'random'; sheetId?: string };
    count: number;
    percentage: number;
  }>;
  isActive: boolean;
}

interface SheetDetails {
  id: string;
  name: string;
  description: string;
}

const TEAM_PROGRESS_BG: Record<string, string> = {
  BLUE: 'bg-blue-500',
  RED: 'bg-red-500',
  GREEN: 'bg-green-500',
  YELLOW: 'bg-yellow-500',
};

/**
 * Sheet Vote Display (Modalità N)
 *
 * Mostra votazioni schede personaggio per tutti i team in tempo reale.
 * Updates via polling ogni 2 secondi.
 */
function SheetVoteDisplay({ sessionId, showClosedVotes = false, activeTeam }: SheetVoteDisplayProps) {
  const { i18n } = useTranslation(['display', 'common']);
  const [votes, setVotes] = useState<VoteResults[]>([]);
  const [sheets, setSheets] = useState<Map<string, SheetDetails>>(new Map());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVotes();
    const interval = setInterval(fetchVotes, 2000);
    return () => clearInterval(interval);
  }, [sessionId, showClosedVotes, activeTeam]);

  const fetchVotes = async () => {
    try {
      // If showing closed votes, get all votes and filter for latest closed per team
      // Otherwise, get active votes
      const endpoint = showClosedVotes
        ? `/api/sheet-votes/session/${sessionId}/all`
        : `/api/sheet-votes/session/${sessionId}/active`;

      const response = await fetch(endpoint);
      if (response.ok) {
        let data = await response.json();

        // If showing closed votes, filter to get only the latest closed vote per team
        if (showClosedVotes) {
          const latestVotesByTeam = new Map<string, any>();
          for (const vote of (data.votes || [])) {
            if (vote.isActive === false && vote.closedAt) {
              const existing = latestVotesByTeam.get(vote.teamColor);
              if (!existing || new Date(vote.closedAt) > new Date(existing.closedAt)) {
                latestVotesByTeam.set(vote.teamColor, vote);
              }
            }
          }
          data = { votes: Array.from(latestVotesByTeam.values()) };
        }

        // Fetch results for each vote
        const votesWithResults = await Promise.all(
          (data.votes || []).map(async (vote: any) => {
            const resultsResponse = await fetch(`/api/sheet-votes/${vote.id}/results`);
            if (resultsResponse.ok) {
              return await resultsResponse.json();
            }
            return null;
          })
        );

        let validVotes = votesWithResults.filter(Boolean);

        // Filter by activeTeam if in SEQUENTIAL mode
        if (activeTeam) {
          validVotes = validVotes.filter((v: VoteResults) => v.teamColor === activeTeam);
        }

        setVotes(validVotes);

        // Fetch sheet details for all sheet options
        const newSheets = new Map(sheets);
        for (const vote of validVotes) {
          for (const result of vote.results) {
            if (result.option.type === 'sheet' && result.option.sheetId) {
              const sheetId = result.option.sheetId;
              if (!newSheets.has(sheetId)) {
                try {
                  const sheetResponse = await fetch(`/api/sheets/${sheetId}`);
                  if (sheetResponse.ok) {
                    const sheetData = await sheetResponse.json();
                    newSheets.set(sheetId, sheetData.sheet);
                  }
                } catch (err) {
                  console.error(`Failed to fetch sheet ${sheetId}:`, err);
                }
              }
            }
          }
        }
        setSheets(newSheets);
      }
    } catch (error) {
      console.error('Failed to fetch vote data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center display-dark-bg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-8 border-parchment-300 mx-auto mb-8"></div>
          <p className="text-3xl text-parchment-200">
            {i18n.language === 'it' ? 'Caricamento votazioni...' : 'Loading Votes...'}
          </p>
        </div>
      </div>
    );
  }

  if (votes.length === 0) {
    return (
      <div className="h-screen flex items-center justify-center display-dark-bg">
        <div className="text-center">
          <h1 className="text-6xl font-bold text-parchment-100 font-display mb-4">
            {i18n.language === 'it' ? 'Selezione Personaggi' : 'Character Selection'}
          </h1>
          <p className="text-3xl text-parchment-400">
            {i18n.language === 'it' ? 'In attesa della votazione...' : 'Waiting for voting to start...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col display-dark-bg p-8 overflow-hidden">
      {/* Header */}
      <div className="text-center mb-6 flex-shrink-0">
        <h1 className="text-6xl font-bold text-parchment-100 font-display mb-2">
          {i18n.language === 'it' ? 'Selezione Personaggi' : 'Character Selection'}
        </h1>
        <p className="text-2xl text-parchment-200">
          {i18n.language === 'it' ? 'Scegli i tuoi eroi!' : 'Choose your heroes!'}
        </p>
      </div>

      {/* Votes Grid - Takes remaining space */}
      <div className={`grid ${votes.length === 1 ? 'grid-cols-1' : votes.length === 2 ? 'grid-cols-2' : votes.length === 3 ? 'grid-cols-3' : 'grid-cols-2'} gap-6 max-w-7xl mx-auto w-full flex-1 overflow-hidden`}>
        {votes.map((vote) => {
          const teamBorder = DISPLAY_TEAM_BORDER[vote.teamColor];
          const teamText = DISPLAY_TEAM_TEXT[vote.teamColor];
          const progressBg = TEAM_PROGRESS_BG[vote.teamColor];

          return (
            <div
              key={vote.voteId}
              className={`display-card border-4 ${teamBorder} shadow-2xl flex flex-col h-full overflow-hidden p-4`}
            >
              {/* Team Header */}
              <div className="text-center mb-3 flex-shrink-0">
                <h2 className={`text-4xl font-heading font-bold ${teamText} uppercase mb-1`}>
                  {TEAM_FACTION_NAMES[vote.teamColor]}
                </h2>
                {vote.isActive && (
                  <div className="mt-1 flex items-center justify-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-green-400 text-xl font-semibold">
                      {i18n.language === 'it' ? 'VOTAZIONE ATTIVA' : 'VOTING ACTIVE'}
                    </span>
                  </div>
                )}
              </div>

              {/* Vote Options - Different rendering based on voting status */}
              <div className="space-y-3 flex-1 overflow-hidden">
                {vote.isActive ? (
                  /* FASE 1: Votazione aperta - Solo nomi personaggi */
                  vote.results.map((result, idx) => {
                    const sheetName = result.option.type === 'random'
                      ? (i18n.language === 'it' ? '🎲 Casuale' : '🎲 Random')
                      : sheets.get(result.option.sheetId!)?.name || `Sheet ${idx + 1}`;

                    return (
                      <div
                        key={idx}
                        className="display-panel p-4"
                      >
                        <div className="text-center">
                          <span className="text-2xl font-heading font-bold text-parchment-100">
                            {sheetName}
                          </span>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  /* FASE 2: Votazione chiusa - Mostra risultati */
                  vote.results.map((result, idx) => {
                    const sheetName = result.option.type === 'random'
                      ? (i18n.language === 'it' ? '🎲 Casuale' : '🎲 Random')
                      : sheets.get(result.option.sheetId!)?.name || `Sheet ${idx + 1}`;

                    return (
                      <div
                        key={idx}
                        className="display-panel p-3"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xl font-heading font-semibold text-parchment-100">
                            {sheetName}
                          </span>
                          <span className="text-2xl font-bold text-parchment-100">
                            {result.percentage.toFixed(0)}%
                          </span>
                        </div>

                        {/* Progress Bar */}
                        <div className="h-6 bg-parchment-500/30 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${progressBg} transition-all duration-500 ease-out`}
                            style={{ width: `${result.percentage}%` }}
                          ></div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      {votes.some((v) => v.isActive) && (
        <div className="mt-4 text-center flex-shrink-0">
          <p className="text-2xl text-parchment-400 animate-pulse">
            {i18n.language === 'it'
              ? 'Votazione in corso... I risultati si aggiorneranno automaticamente'
              : 'Voting in progress... Results update in real-time'}
          </p>
        </div>
      )}
    </div>
  );
}

export default SheetVoteDisplay;
